import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import { ChevronDown, Clock, Plus, Trash2 } from "lucide-react";
import CustomToast from "../../components/misc/CustomToast";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/misc/Card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/misc/Table";
import { Button } from "../../components/misc/Button";
import Spinner from "../../components/misc/Spinner";
import { useIsMobile } from "../../hooks/useIsMobile";
import { cn } from "../../utils/classMerger";
import { getAppConfigs, resetAppConfig, updateAppConfig } from "../app-config/actions";
import { AppConfigItem } from "../app-config/types";
import {
  POST_RETRY_BACKOFF_MINUTES_KEY,
  POST_RETRY_CONFIG_GROUP,
  POST_RETRY_MAX_RETRIES_KEY,
} from "./types";

const DEFAULT_WAIT_MIN = 10;
const DEFAULT_MAX_ROWS = 10;
const DEFAULT_DESCRIPTION =
  "When a publish hits a transient failure, Marabot retries on this schedule. After the last attempt fails, the post is marked Error.";

interface RetrySettingsPanelProps {
  // Heading shown only before the backend responds with its own group label.
  fallbackTitle?: string;
}

const extractApiError = (err: unknown, fallback: string): string => {
  const axErr = err as AxiosError<{ message?: string | string[] }>;
  const msg = axErr?.response?.data?.message;
  if (Array.isArray(msg)) return msg.join(", ");
  return msg ?? fallback;
};

const toRows = (value: unknown): string[] =>
  Array.isArray(value) ? value.map((v) => String(v)) : [];

const errorToast = (message: string): void => {
  toast.error(<CustomToast message={message} type="error" />, {
    autoClose: 3500,
  });
};

const RetrySettingsPanel: React.FC<RetrySettingsPanelProps> = ({
  fallbackTitle = "Retry settings",
}) => {
  const mobile = useIsMobile(768);

  const [backoffItem, setBackoffItem] = useState<AppConfigItem | null>(null);
  const [maxItem, setMaxItem] = useState<AppConfigItem | null>(null);
  const [rows, setRows] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState(true);

  const applyItems = useCallback((items: AppConfigItem[]): void => {
    const backoff =
      items.find((i) => i.key === POST_RETRY_BACKOFF_MINUTES_KEY) ?? null;
    const max =
      items.find((i) => i.key === POST_RETRY_MAX_RETRIES_KEY) ?? null;
    setBackoffItem(backoff);
    setMaxItem(max);
    if (backoff) setRows(toRows(backoff.value));
  }, []);

  const loadConfigs = useCallback(async (): Promise<void> => {
    try {
      const items = await getAppConfigs(POST_RETRY_CONFIG_GROUP);
      applyItems(items);
    } catch (err) {
      errorToast(extractApiError(err, "Failed to load retry settings"));
    }
  }, [applyItems]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadConfigs();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [loadConfigs]);

  const waitMin = backoffItem?.min ?? DEFAULT_WAIT_MIN;
  const waitMax = backoffItem?.max;
  const minRows = Math.max(backoffItem?.minItems ?? 1, 1);
  const maxRows = Math.min(
    backoffItem?.maxItems ?? DEFAULT_MAX_ROWS,
    maxItem?.max ?? DEFAULT_MAX_ROWS,
  );

  const numericRows = useMemo(() => rows.map((r) => Number(r)), [rows]);

  // Running total after each attempt: "Fires after failure" = sum of waits up to and including it.
  const cumulative = useMemo(() => {
    let sum = 0;
    return numericRows.map((n) => {
      sum += Number.isFinite(n) ? n : 0;
      return sum;
    });
  }, [numericRows]);
  const total = cumulative.length ? cumulative[cumulative.length - 1] : 0;

  const isAtDefault = useMemo(
    () =>
      !!backoffItem &&
      JSON.stringify(backoffItem.value) ===
        JSON.stringify(backoffItem.defaultValue) &&
      Number(maxItem?.value) === Number(maxItem?.defaultValue),
    [backoffItem, maxItem],
  );

  const updateRow = (index: number, raw: string): void => {
    const digits = raw.replace(/[^0-9]/g, "");
    setRows((prev) => prev.map((r, i) => (i === index ? digits : r)));
  };

  const addRow = (): void => {
    if (rows.length >= maxRows) return;
    setRows((prev) => [...prev, ""]);
  };

  const removeRow = (index: number): void => {
    if (rows.length <= minRows) return;
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = (): string | null => {
    if (rows.length < minRows)
      return `Add at least ${minRows} ${minRows === 1 ? "retry" : "retries"}.`;
    if (rows.length > maxRows) return `Keep ${maxRows} retries or fewer.`;
    for (let i = 0; i < rows.length; i++) {
      const raw = rows[i].trim();
      if (raw === "") return `Enter a wait time for Retry ${i + 1}.`;
      const value = Number(raw);
      if (!Number.isInteger(value) || value <= 0)
        return `Retry ${i + 1} must be a positive whole number of minutes.`;
      if (value < waitMin)
        return `Each wait must be at least ${waitMin} minutes.`;
      if (waitMax !== undefined && value > waitMax)
        return `Each wait must be at most ${waitMax} minutes.`;
    }
    return null;
  };

  const handleSave = async (): Promise<void> => {
    if (busy) return;
    const error = validate();
    if (error) {
      errorToast(error);
      return;
    }
    setBusy(true);
    try {
      const [updatedBackoff, updatedMax] = await Promise.all([
        updateAppConfig(POST_RETRY_BACKOFF_MINUTES_KEY, numericRows),
        updateAppConfig(POST_RETRY_MAX_RETRIES_KEY, numericRows.length),
      ]);
      setBackoffItem(updatedBackoff);
      setMaxItem(updatedMax);
      setRows(toRows(updatedBackoff.value));
      toast.success(
        <CustomToast message="Retry schedule saved." type="success" />,
        { autoClose: 3000 },
      );
    } catch (err) {
      errorToast(extractApiError(err, "Failed to save retry schedule"));
      // One key may have persisted before the other failed — reload so the UI reflects the truth.
      await loadConfigs();
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      const [resetBackoff, resetMax] = await Promise.all([
        resetAppConfig(POST_RETRY_BACKOFF_MINUTES_KEY),
        resetAppConfig(POST_RETRY_MAX_RETRIES_KEY),
      ]);
      setBackoffItem(resetBackoff);
      setMaxItem(resetMax);
      setRows(toRows(resetBackoff.value));
      toast.success(
        <CustomToast
          message="Retry schedule reset to default."
          type="success"
        />,
        { autoClose: 3000 },
      );
    } catch (err) {
      errorToast(extractApiError(err, "Failed to reset retry schedule"));
      await loadConfigs();
    } finally {
      setBusy(false);
    }
  };

  // Config genuinely absent after a successful load — render nothing.
  if (!loading && (!backoffItem || !maxItem)) return null;

  const title = backoffItem?.groupLabel || fallbackTitle;
  const description = backoffItem?.groupDescription || DEFAULT_DESCRIPTION;

  const toggle = (): void => setCollapsed((c) => !c);

  const inputClass = cn(
    "w-20 rounded-md border border-gray-300 px-3 py-2 text-sm text-center",
    "focus:outline-none focus:ring-2 focus:ring-primary",
    "disabled:bg-gray-50 disabled:text-gray-400",
  );

  const trashButton = (index: number): JSX.Element => (
    <button
      type="button"
      onClick={() => removeRow(index)}
      disabled={busy || rows.length <= minRows}
      aria-label={`Remove retry ${index + 1}`}
      className={cn(
        "inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400 transition-colors",
        "hover:bg-gray-100 hover:text-[#d50000]",
        "disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gray-400",
      )}
    >
      <Trash2 className="h-4 w-4" />
    </button>
  );

  return (
    <Card>
      <CardHeader
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle();
          }
        }}
        className={cn("cursor-pointer select-none", !collapsed && "pb-2")}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1.5">
            <CardTitle className="text-left">{title}</CardTitle>
            <p className="text-sm text-gray-500">{description}</p>
          </div>
          <ChevronDown
            className={cn(
              "mt-0.5 h-5 w-5 shrink-0 text-gray-400 transition-transform duration-200",
              !collapsed && "rotate-180",
            )}
          />
        </div>
      </CardHeader>

      {!collapsed && (
        <CardContent className="pt-0">
          {loading ? (
            <div className="py-6">
              <Spinner loadingMessage="Loading retry settings..." />
            </div>
          ) : (
            <div className="max-w-2xl">
              {mobile ? (
                <div className="grid gap-1">
                  {rows.map((row, i) => (
                    <div
                      key={i}
                      className="flex flex-col gap-2 border-b border-gray-100 py-3 last:border-0"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-primary">
                          Retry {i + 1}
                        </span>
                        {trashButton(i)}
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          inputMode="numeric"
                          min={waitMin}
                          max={waitMax}
                          value={row}
                          disabled={busy}
                          onChange={(e) => updateRow(i, e.target.value)}
                          aria-label={`Retry ${i + 1} wait in minutes`}
                          className={inputClass}
                        />
                        <span className="text-sm text-gray-500">min</span>
                        <span className="ml-auto flex items-center gap-1.5 text-xs text-gray-500">
                          <Clock className="h-3.5 w-3.5 text-gray-400" />~
                          {cumulative[i]} min
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Attempt</TableHead>
                      <TableHead>Wait before it</TableHead>
                      <TableHead>Fires after failure</TableHead>
                      <TableHead className="w-12">
                        <span className="sr-only">Actions</span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((row, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-semibold text-primary">
                          Retry {i + 1}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              inputMode="numeric"
                              min={waitMin}
                              max={waitMax}
                              value={row}
                              disabled={busy}
                              onChange={(e) => updateRow(i, e.target.value)}
                              aria-label={`Retry ${i + 1} wait in minutes`}
                              className={inputClass}
                            />
                            <span className="text-sm text-gray-500">min</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="flex items-center gap-2 text-gray-600">
                            <Clock className="h-4 w-4 text-gray-400" />~
                            {cumulative[i]} min
                          </span>
                        </TableCell>
                        <TableCell>{trashButton(i)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              <div
                className={cn(
                  "mt-4 flex items-center justify-between gap-3",
                  mobile && "flex-col items-stretch",
                )}
              >
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addRow}
                  disabled={busy || rows.length >= maxRows}
                  className={cn(
                    "border-gray-300 text-gray-700",
                    mobile && "w-full",
                  )}
                >
                  <Plus className="h-4 w-4" />
                  Add retry
                </Button>
                <span
                  className={cn(
                    "text-sm text-gray-500",
                    mobile && "text-center",
                  )}
                >
                  {rows.length} {rows.length === 1 ? "retry" : "retries"} over ~
                  {total} min
                </span>
              </div>

              <div className="mt-6 flex items-center gap-3 border-t border-gray-100 pt-5">
                <Button
                  onClick={handleSave}
                  disabled={busy}
                  className={cn(mobile && "flex-1")}
                >
                  {busy ? "Saving..." : "Save"}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleReset}
                  disabled={busy || isAtDefault}
                  className={cn(
                    "border-gray-300 text-gray-700",
                    mobile && "flex-1",
                  )}
                >
                  Reset to default
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default RetrySettingsPanel;
